/* eslint-disable no-console */
import { useCallback, useEffect, useReducer, useRef } from 'react'
import { aquaFetch } from 'common/utils/ActionUtils'
import useTimeout from './useTimeout'
import moment from 'moment'
import { STATUS } from 'common/constants/storeConstants'

const initialState = {
    error: undefined,
    data: undefined,
    resultDate: undefined,
    status: STATUS.IDLE,
}

const useFetchTimeout = (url, options, delay, timeoutOptions) => {
    // Used to prevent state update if the component is unmounted
    const cancelRequest = useRef(false)

    const {
        skipLoadingState = false,
        formatResult = r => r,
    } = timeoutOptions ?? {}

    // Keep state logic separated
    const fetchReducer = (state, action) => {
        switch (action.type) {
            case 'loading':
                return { ...initialState, status: STATUS.LOADING, data: state.data, resultDate: state.resultDate }
            case 'fetched':
                return { ...initialState, status: STATUS.SUCCEEDED, data: action.payload, resultDate: moment().valueOf() }
            case 'error':
                return { ...initialState, status: STATUS.REJECTED, error: action.payload, resultDate: moment().valueOf() }
            default:
                return state
        }
    }

    const [state, dispatch] = useReducer(fetchReducer, initialState)

    const fetchData = async () => {
        cancelRequest.current = false
        if (!skipLoadingState) {
            dispatch({ type: 'loading' })
        }

        try {
            const promiseResult = await aquaFetch(url, options)
                .then(formatResult)
                .catch(e => {
                    throw new Error(e)
                })

            if (cancelRequest.current) {
                return
            }

            dispatch({ type: 'fetched', payload: promiseResult })
        } catch (error) {
            if (cancelRequest.current) {
                return
            }

            console.error(error)
            dispatch({ type: 'error', payload: error.message })
        }
    }

    useEffect(() => {
        // Use the cleanup function for avoiding a possibly
        // state update after the component was unmounted
        if (url) {
            fetchData()
        }
        return () => {
            cancelRequest.current = true
        }
    }, [url])

    const { reset, clear } = useTimeout(() => {
        if (!url) {
            return
        }
        fetchData().finally(reset)
    }, delay)

    const resetTimeout = useCallback(() => {
        if (url) {
            fetchData()
        }
        reset()
    }, [url, reset])

    return {
        ...state,
        reset: resetTimeout,
        reload: fetchData,
        clear,
    }
}

export default useFetchTimeout